import AccommodationCalendarModal from 'components/global/CalendarModal/forks/AccommodationCalendarModal'
import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Datepicker from 'components/shared/Datepicker'
import Section from 'components/shared/Section'
import Select from 'components/shared/Select'
import Text from 'components/shared/Text'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
    updateGuestsToAssign,
    setAssignGuestsVisible,
    setBungalowsVisible,
    setBungalows,
    emptyBungalow,
    removeAllRooms,
} from 'store/accommodation-slice'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import { useAutoScroll } from 'utils/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { DatePickerWrapper, InputWrapper } from './styles'
import { pushDataLayer } from 'helpers/helpers'
import { useApp } from 'store/steps'
import AccommodationInfoContainer from 'components/shared/Promotions/components/AccommodationInfoContainer'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { CityBreakFoldableInfo } from 'components/common/CityBreak/components/CityBreakFoldableInfo'

const selectOptions = Array.from({ length: 19 }).map((_, index) => ({
    option: `${index}`,
}))

const PlanYourStay = () => {
    const dispatch = useAppDispatch()
    const { isCreatorAccommodation } = useApp()
    const { calculateScroll } = useAutoScroll()
    const { isCityBreakPromoActive } = usePromotions()

    const {
        guestsToAssign,
        dates,
        areBungalowsVisible,
        isAssignGuestsVisible,
    } = useAppSelector((state) => state.accommodation)
    const cartAccommodation = useAppSelector(
        (state) => state.cart.items.accommodation
    )
    const { removeAccommodationFromCart } = useAccommodation(
        cartAccommodation?.productId
    )
    const { adults, kids, infants } = guestsToAssign
    const { formatMessage } = useIntl()
    const [isCalendarOpened, setIsCalendarOpened] = useState(false)

    const handleUpdateGuestsNumber = async (
        selectedValue: number,
        type: 'adults' | 'kids' | 'infants'
    ) => {
        let updatedState
        switch (type) {
            case 'adults':
                updatedState = {
                    total: selectedValue + kids + infants,
                    adults: selectedValue,
                    kids,
                    infants,
                }
                break
            case 'kids':
                updatedState = {
                    total: selectedValue + adults + infants,
                    adults,
                    kids: selectedValue,
                    infants,
                }
                break
            case 'infants':
                updatedState = {
                    total: selectedValue + adults + kids,
                    adults,
                    kids,
                    infants: selectedValue,
                }
                break
        }

        if (cartAccommodation) {
            await removeAccommodationFromCart({ editPoint: 'guests' })
        }

        dispatch(removeAllRooms())

        if (areBungalowsVisible) {
            dispatch(setBungalowsVisible(false))
        }
        if (isAssignGuestsVisible) {
            dispatch(setAssignGuestsVisible(false))
            dispatch(setBungalows([emptyBungalow]))
        }
        dispatch(updateGuestsToAssign(updatedState))
    }

    const handleContinue = () => {
        dispatch(setAssignGuestsVisible(true))
        calculateScroll({
            trigerringSection: 'accommodation',
            trigerringAction: 'continue_planning',
        })

        // < analytics
        isCreatorAccommodation &&
            pushDataLayer({
                event: 'funnelStep',
                event_category: 'accommodation',
                event_action: 'arrival_params_selected',
                event_label: '',
            })
        // analytics >
    }

    return (
        <Section id="planYourStay">
            <Box gap={[16, 24]} mb={24} isFullWidth>
                <Text variant="heading2" color="grayscale_900">
                    <FormattedMessage
                        id="accommodation.plan_your_stay.section_caption"
                        defaultMessage="Zaplanuj pobyt"
                    />
                </Text>

                {isCityBreakPromoActive ? (
                    <Box mt={[24, 32]} mb={[0, 8]} isFullWidth>
                        <CityBreakFoldableInfo />
                    </Box>
                ) : (
                    <AccommodationInfoContainer />
                )}
            </Box>
            <Box
                direction={['column', 'row']}
                isFullWidth
                gap={[32, 16]}
                mb={24}
            >
                <DatePickerWrapper gap={[16, 48]} isFullWidth>
                    <Text variant="heading4" color="grayscale_900">
                        <FormattedMessage
                            id="accommodation.plan_your_stay.visit_date_caption"
                            defaultMessage="Termin wizyty"
                        />
                    </Text>
                    <Datepicker
                        id="date-of-visit-accomodation"
                        placeholder={formatMessage({
                            id: 'global.input.select_date_placeholder',
                            defaultMessage: 'Wybierz datę',
                        })}
                        value={dates}
                        openCalendar={() => setIsCalendarOpened(true)}
                        testId="accommo-trigger-visit-date"
                    />
                </DatePickerWrapper>
                <Box gap={24} isFullWidth>
                    <Text variant="heading4" color="grayscale_900">
                        <FormattedMessage
                            id="accommodation.plan_your_stay.guests_caption"
                            defaultMessage="Liczba gości"
                        />
                    </Text>
                    <Box
                        direction={['column', 'row']}
                        gap={[24, 16]}
                        isFullWidth
                    >
                        <InputWrapper isFullWidth>
                            <Select
                                isPortal
                                value={adults.toString()}
                                options={selectOptions}
                                id="adults-accomodation"
                                label={formatMessage({
                                    id: 'global.input.adult_label',
                                    defaultMessage: 'Dorosły',
                                })}
                                onChange={(option: string) =>
                                    handleUpdateGuestsNumber(+option, 'adults')
                                }
                                hasNoMaxWidth
                                testId="accommo-select-adult"
                            />
                        </InputWrapper>
                        <InputWrapper isFullWidth>
                            <Select
                                isPortal
                                value={kids.toString()}
                                options={selectOptions}
                                id="kids-accomodation"
                                label={formatMessage({
                                    id: 'global.input.child_under_12_label',
                                    defaultMessage: 'Dziecko (do 12 lat)',
                                })}
                                onChange={(option: string) =>
                                    handleUpdateGuestsNumber(+option, 'kids')
                                }
                                hasNoMaxWidth
                                testId="accommo-select-kids"
                            />
                        </InputWrapper>
                        <InputWrapper
                            align={['flex-start', 'flex-end']}
                            isFullWidth
                        >
                            <Select
                                isPortal
                                value={infants.toString()}
                                options={selectOptions}
                                id="small-kids-accommodation"
                                label={formatMessage({
                                    id: 'global.input.child_under_3_label',
                                    defaultMessage: 'Dziecko (do 3 lat)',
                                })}
                                onChange={(option: string) =>
                                    handleUpdateGuestsNumber(+option, 'infants')
                                }
                                hasNoMaxWidth
                                testId="accommo-select-infants"
                            />
                            <Text variant="bodyExtraSmall">
                                <FormattedMessage
                                    id="global.input.legend_free"
                                    defaultMessage="Za darmo!"
                                />
                            </Text>
                        </InputWrapper>
                    </Box>
                </Box>
            </Box>
            <Box direction="row" justify={['stretch', 'flex-end']}>
                <Button
                    type="button"
                    onClick={handleContinue}
                    variant="secondary"
                    isDisabled={dates.length === 0 || adults === 0}
                    width={['100%', 'initial']}
                    testId="accommo-button-continue config"
                >
                    <FormattedMessage
                        id="global.btn.continue"
                        defaultMessage="Kontynuuj"
                    />
                </Button>
            </Box>
            <AccommodationCalendarModal
                isOpened={isCalendarOpened}
                close={() => setIsCalendarOpened(false)}
            />
        </Section>
    )
}
export default PlanYourStay
